import { Injectable, inject } from '@angular/core';
import { fromEvent, merge, Subscription, animationFrameScheduler } from 'rxjs';
import { auditTime, filter } from 'rxjs/operators';
import { OverlayCloseService } from './overlay-close.service';

// Selectors of dropdown-like overlay panels OR of the input controls that
// own them. Scrolls/wheels happening inside any of these should NOT trigger
// auto-close. This covers two cases:
//   1) User wheel-scrolls the open suggestion list (`.ant-select-dropdown`).
//   2) User types into a multi-select search input, which causes the internal
//      `.ant-select-selection-overflow` container to scroll horizontally as
//      tags + search-mirror grow. Without the input-side selectors below,
//      wide letters (a, b, c, …) would scroll the overflow container and
//      close the dropdown; narrow letters (l, i) would not — leading to a
//      very confusing "dropdown disappears only for some letters" bug.
// Scrolls happening anywhere else — page, modal body, etc. — still close
// the dropdowns (intended UX: closing on real page scroll).
const DROPDOWN_PANEL_SELECTORS = [
  // Open overlay panels
  '.ant-select-dropdown',
  '.ant-picker-dropdown',
  '.ant-dropdown',
  '.ant-cascader-dropdown',
  '.ant-mention-dropdown',
  // Input controls that own those panels (their internal scrolls must be
  // treated as "inside the dropdown" too).
  '.ant-select',
  '.ant-picker',
  '.ant-cascader-picker',
].join(', ');

@Injectable({ providedIn: 'root' })
export class OverlayScrollAutocloseService {
  private readonly overlayCloseService = inject(OverlayCloseService);
  private scrollSub: Subscription | null = null;

  start(): void {
    if (this.scrollSub || typeof document === 'undefined') {
      return;
    }

    // Listen only to deliberate, user-initiated scroll gestures: `wheel`
    // (desktop) and `touchmove` (touch devices). We intentionally do NOT
    // listen to the generic `scroll` event, because it also fires for
    // programmatic/focus-driven scrolling. On mobile, tapping an
    // `nz-select` / `nz-date-picker` focuses its input and brings up the
    // on-screen keyboard, which makes the browser scroll the field into
    // view — firing `scroll` (but not `wheel`/`touchmove`). Closing on that
    // event made the dropdown appear to "never open" on phones. Reacting to
    // `wheel`/`touchmove` keeps the intended UX (close on real page scroll)
    // without the false positives.
    const wheel$ = fromEvent<Event>(document, 'wheel', {
      capture: true,
      passive: true,
    });
    const touchMove$ = fromEvent<Event>(document, 'touchmove', {
      capture: true,
      passive: true,
    });

    this.scrollSub = merge(wheel$, touchMove$)
      .pipe(
        filter(event => !this.isInsideDropdownPanel(event)),
        auditTime(0, animationFrameScheduler),
      )
      .subscribe(() => {
        this.overlayCloseService.closeOpenDropdownLikeOverlays();
      });
  }

  stop(): void {
    this.scrollSub?.unsubscribe();
    this.scrollSub = null;
  }

  /**
   * Returns true when the scroll/wheel event originates inside an open
   * dropdown panel (e.g. the user is wheel-scrolling the autocomplete option
   * list). Modal body scrolls do NOT match this selector even though they
   * live inside `.cdk-overlay-container`, so they will still close dropdowns.
   */
  private isInsideDropdownPanel(event: Event): boolean {
    const target = event.target;
    if (!(target instanceof Node)) {
      return false;
    }
    const element =
      target.nodeType === Node.ELEMENT_NODE ? (target as Element) : target.parentElement;
    return !!element?.closest(DROPDOWN_PANEL_SELECTORS);
  }
}
